import { useEffect, useMemo, useState } from "react";
import type { GraphyClient } from "../api/client";
import type { FindingStatus, NodeRef, Severity, SnapshotRef, SystemMapData, SystemMapNode } from "../api/types";
import type { ViewState } from "../state/viewState";
import { MapCanvas } from "./MapCanvas";
import { MapLegend } from "../components/Legend";
import { Inspector } from "../components/Inspector";
import { SourceDrawer } from "../components/SourceDrawer";
import { EmptyState, ErrorState, Loading, PartialBanner } from "../components/states";
import { Icon } from "../components/icons";
import { communityTones, SEVERITY_COLOR, SEVERITY_ORDER } from "../graph/palette";
import { T } from "../i18n";

// System map: every type in the snapshot (classes, interfaces, records, enums)
// and the dependencies between them, derived from their methods' calls. The
// method-level graph lives in the Explorer. Filters fade non-matching types
// instead of removing them, so the surrounding structure stays readable.

interface Props {
  client: GraphyClient;
  projectId: string;
  snapshot: SnapshotRef | null;
  nodes: NodeRef[]; // method-level nodes (used for labels in the drawer)
  state: ViewState;
  patch: (p: Partial<ViewState>) => void;
  onDrillDown: (id: string) => void;
  onViewSource: (id: string) => void;
}

const STATUSES: FindingStatus[] = ["open", "fixed", "partial", "retest"];

/** "agents/software-engineer/src/main/java/io/x/agent2/service/Foo.java" -> "software-engineer · service" */
function areaOf(file?: string) {
  if (!file) return "";
  const parts = file.split("/");
  const module = parts[0] === "agents" && parts[1] ? parts[1] : parts[0];
  const pkg = parts.length > 2 ? parts[parts.length - 2] : "";
  return pkg && pkg !== module ? `${module} · ${pkg}` : module;
}

export function SystemMap({ client, projectId, snapshot, nodes: methodNodes, state, patch, onDrillDown }: Props) {
  const snapshotId = snapshot?.snapshotId ?? "";
  const scope = { projectId, snapshotId };
  const { mapSearch, mapSeverity, mapStatus, selected, affected } = state;

  const [data, setData] = useState<SystemMapData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [focusRequest, setFocusRequest] = useState<{ id: string; seq: number } | null>(null);
  const [sourceFor, setSourceFor] = useState<string | null>(null);

  useEffect(() => {
    if (!snapshotId) { setData(null); return; }
    let live = true;
    setLoading(true);
    setError(false);
    client
      .systemMap(scope)
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setError(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotId, reload]);

  const nodes = data?.nodes ?? [];
  const edges = data?.edges ?? [];
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const tone = useMemo(() => communityTones(nodes.map((n) => n.community)), [nodes]);
  const colorOf = useMemo(() => (n: SystemMapNode) => tone(n.community), [tone]);

  const upstreamOf = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const [s, t] of edges) m.set(t, [...(m.get(t) ?? []), s]);
    return m;
  }, [edges]);
  const downstreamOf = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const [s, t] of edges) m.set(s, [...(m.get(s) ?? []), t]);
    return m;
  }, [edges]);

  // Filter -> matching ids (null when no filter is active).
  const matches = useMemo(() => {
    const q = mapSearch.trim().toLowerCase();
    if (!q && mapSeverity === "all" && mapStatus === "all") return null;
    return new Set(
      nodes
        .filter((n) =>
          (mapSeverity === "all" || n.severity === mapSeverity) &&
          (mapStatus === "all" || n.findingStatus === mapStatus) &&
          (!q || `${n.label} ${n.file ?? ""}`.toLowerCase().includes(q)))
        .map((n) => n.id),
    );
  }, [nodes, mapSearch, mapSeverity, mapStatus]);

  // Affected set on the type graph: every type that transitively depends on the
  // selected one (it would be affected by a change or a flaw there).
  const affectedSet = useMemo(() => {
    if (!affected || !selected || !byId.has(selected)) return null;
    const seen = new Set<string>();
    const queue = [selected];
    while (queue.length) {
      const id = queue.shift()!;
      for (const up of upstreamOf.get(id) ?? []) {
        if (!seen.has(up) && up !== selected) { seen.add(up); queue.push(up); }
      }
    }
    return seen;
  }, [affected, selected, byId, upstreamOf]);

  const hubs = useMemo(() => [...nodes].sort((a, b) => b.degree - a.degree).slice(0, 10), [nodes]);
  const severitiesPresent = SEVERITY_ORDER.filter((s) => nodes.some((n) => n.severity === s));
  const findingTypes = nodes.filter((n) => n.severity).length;
  const communityCount = useMemo(() => new Set(nodes.map((n) => n.community ?? -1)).size, [nodes]);
  const withSource = nodes.filter((n) => n.hasSource).length;

  if (!snapshot) return <EmptyState message={T.common.noSnapshot} />;

  const focusOn = (id: string) => {
    patch({ selected: id });
    setFocusRequest({ id, seq: Date.now() });
  };

  const selectedNode = selected ? byId.get(selected) ?? null : null;
  const methodLabel = (id: string) => byId.get(id)?.label ?? methodNodes.find((n) => n.id === id)?.label ?? id;

  const stats: { label: string; value: string | number; tone?: string }[] = [
    { label: "Types", value: nodes.length },
    { label: "Dependencies", value: edges.length + (data?.truncated ? "+" : "") },
    { label: "Communities", value: communityCount },
    { label: "With findings", value: findingTypes, tone: findingTypes ? SEVERITY_COLOR[severitiesPresent[0]] : undefined },
  ];

  return (
    <div className="map">
      <div className="map-stats">
        {stats.map((s) => (
          <div className="stat-cell" key={s.label}>
            <span className="stat-cell-label">{s.label}</span>
            <span className="stat-cell-value" style={s.tone ? { color: s.tone } : undefined}>{s.value}</span>
          </div>
        ))}
        <div className="stat-cell stat-cell-wide">
          <span className="stat-cell-label">{T.map.coverage}</span>
          <span className="stat-cell-repos" title={snapshot.repos.join(" · ")}>{snapshot.repos.join(" · ")}</span>
          <span className="stat-cell-sub">Source indexed for {withSource} of {nodes.length} types</span>
        </div>
      </div>

      <div className="map-controls">
        <label className="search">
          <div className="field-search">
            <Icon.search size={15} />
            <input aria-label={T.map.search} value={mapSearch} onChange={(e) => patch({ mapSearch: e.target.value })} placeholder="Search types or files…" />
          </div>
        </label>
        <label title={severitiesPresent.length ? undefined : "No confirmed findings in this snapshot"}>
          {T.map.severity}
          <select
            aria-label={T.map.severity}
            value={mapSeverity}
            disabled={!severitiesPresent.length}
            onChange={(e) => patch({ mapSeverity: e.target.value as Severity | "all" })}
          >
            <option value="all">{T.map.all}</option>
            {SEVERITY_ORDER.map((s) => <option key={s} value={s} disabled={!severitiesPresent.includes(s)}>{s}</option>)}
          </select>
        </label>
        <label title={severitiesPresent.length ? undefined : "No confirmed findings in this snapshot"}>
          {T.map.status}
          <select aria-label={T.map.status} value={mapStatus} disabled={!severitiesPresent.length} onChange={(e) => patch({ mapStatus: e.target.value as FindingStatus | "all" })}>
            <option value="all">{T.map.all}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        {matches && (
          <span className="map-filter-count">
            {matches.size} of {nodes.length} match
            <button className="link" onClick={() => patch({ mapSearch: "", mapSeverity: "all", mapStatus: "all" })}>Clear</button>
          </span>
        )}
      </div>

      {data?.truncated && <PartialBanner />}

      <div className="map-layout">
        <section className="map-main">
          {error ? (
            <ErrorState onRetry={() => setReload((r) => r + 1)} />
          ) : loading || !data ? (
            <Loading />
          ) : nodes.length === 0 ? (
            <EmptyState message="No types were found in this snapshot." />
          ) : (
            <>
              <MapCanvas
                nodes={nodes}
                edges={edges}
                colorOf={colorOf}
                selected={selected && byId.has(selected) ? selected : null}
                onSelect={(id) => patch({ selected: id })}
                matches={matches}
                affected={affectedSet}
                focusRequest={focusRequest}
                ariaLabel={T.app.map}
              />
              <MapLegend severities={severitiesPresent} />
            </>
          )}
        </section>

        <div className="map-rail">
          {selectedNode && (
            <Inspector
              node={selectedNode}
              area={areaOf(selectedNode.file)}
              uses={downstreamOf.get(selectedNode.id)?.length ?? 0}
              usedBy={upstreamOf.get(selectedNode.id)?.length ?? 0}
              onDrillDown={onDrillDown}
              onViewSource={setSourceFor}
              affectedActive={affected}
              affectedCount={affectedSet?.size ?? null}
              onToggleAffected={() => patch({ affected: !affected })}
            />
          )}

          {hubs.length > 0 && (
            <aside className="most-connected" aria-label="Most connected">
              <h3>Most connected</h3>
              <ul>
                {hubs.map((h) => (
                  <li key={h.id}>
                    <button className={`mc-item${h.id === selected ? " is-active" : ""}`} onClick={() => focusOn(h.id)} title={h.file ?? h.label}>
                      <span
                        className="mc-dot"
                        style={{ background: h.severity ? SEVERITY_COLOR[h.severity] : colorOf(h) }}
                        aria-hidden="true"
                      />
                      <span className="mc-text">
                        <span className="mc-name">{h.label}</span>
                        <span className="mc-sub">{areaOf(h.file)}</span>
                      </span>
                      <span className="mc-links">{h.degree}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </div>

      {sourceFor && (
        <SourceDrawer
          client={client}
          scope={scope}
          symbolId={sourceFor}
          labelOf={methodLabel}
          hasFinding={!!byId.get(sourceFor)?.severity}
          related={{
            upstreamTitle: "Used by",
            downstreamTitle: "Depends on",
            upstream: upstreamOf.get(sourceFor) ?? [],
            downstream: downstreamOf.get(sourceFor) ?? [],
          }}
          onClose={() => setSourceFor(null)}
          onNavigate={(id) => {
            setSourceFor(null);
            if (byId.has(id)) focusOn(id);
            else onDrillDown(id);
          }}
        />
      )}
    </div>
  );
}
